import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function Login() {
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [debugPin, setDebugPin] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const sendPin = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login-init", { identifier, password });
      setDebugPin(r.data._debug_pin || "");
      toast.success("PIN sent to your email");
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login-verify", { identifier, pin });
      login(r.data.token, r.data.user);
      toast.success("Signed in");
      nav("/matches");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "moderation_locked") {
        const suffix = detail.reason ? ` Reason: ${detail.reason}.` : "";
        toast.error(`${detail.message}${suffix}`, { duration: 10000 });
      } else {
        toast.error(formatApiError(err));
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] flex items-center justify-center px-4 py-12 bg-[#FDFBF7]">
      <Card className="w-full max-w-md p-8 border-[#EAE3D2] shadow-xl bg-white">
        <h1 className="font-serif text-3xl font-semibold text-slate-900">Welcome back</h1>
        <p className="text-sm text-slate-600 mt-1">Sign in with your email or mobile, plus a one-time PIN.</p>

        {step === 1 ? (
          <form onSubmit={sendPin} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="identifier" className="text-slate-700">Email or Mobile</Label>
              <Input id="identifier" data-testid="login-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required className="mt-1" placeholder="you@example.com or +44…" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-send-pin-btn" className="w-full bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full py-6">
              {busy ? "Sending PIN…" : "Send 6-digit PIN"}
            </Button>
            <p className="text-sm text-center text-slate-600">
              <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-[#6B1D2F] font-semibold">Forgot password?</Link>
            </p>
            <p className="text-sm text-center text-slate-600">New here? <Link to="/register" data-testid="login-to-register" className="text-[#6B1D2F] font-semibold">Create a free profile</Link></p>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-6 space-y-4">
            <p className="text-sm text-slate-700">Enter the 6-digit PIN sent to your email.</p>
            {debugPin && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-900" data-testid="login-debug-pin">
                Demo PIN (auto-shown, would be sent by email in production): <b>{debugPin}</b>
              </div>
            )}
            <div className="flex justify-center" data-testid="login-otp-input">
              <InputOTP maxLength={6} value={pin} onChange={setPin}>
                <InputOTPGroup>
                  {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button type="submit" disabled={busy || pin.length !== 6} data-testid="login-verify-btn" className="w-full bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full py-6">
              {busy ? "Verifying…" : "Verify & Sign in"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setStep(1)} className="w-full">Back</Button>
          </form>
        )}
      </Card>
    </div>
  );
}
